import { Link } from "react-router-dom";
import { AlertOctagon, ArrowRight } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const FEATURES = [
  {
    label: "Active cover",
    value: "Growth",
    sub: "KES 250k limit",
    desc: "See your live policy tier, weekly premium, and the upcoming billing date at a glance.",
  },
  {
    label: "CyberScore",
    value: "92",
    sub: "Low risk",
    accent: true,
    desc: "Your risk rating, updated weekly from real claims and fraud signals. Stay ≥ 80 for four weeks to unlock the 10% loyalty rebate.",
  },
  {
    label: "Trust Badge",
    value: "Verified",
    sub: "Live · MW-2026",
    desc: "One-tap snippet to embed your live verifier on any surface. Status pulse turns red if cover lapses.",
  },
];

export default function DashboardPreview() {
  usePageMeta({
    title: "Merchant dashboard preview — Fortiqa",
    description:
      "See every Fortiqa feature merchants get: cover, claims, risk score, payment history, fraud alerts, and the Trust Badge.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="dashboard-preview-page">
      <MarketingPageShell
        eyebrow="Merchant dashboard"
        title="Everything you need in one place."
        intro="Track your cover, monitor FlashClaim payouts, see your CyberScore risk rating, and respond to fraud alerts — all from one mobile-friendly dashboard."
        testid="dashboard-preview"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12">
          <div className="rounded-3xl bg-white border border-[#114232]/10 shadow-xl p-5 sm:p-8">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2.5">
                <img src="/fortiqa-mark.svg" alt="" className="w-7 h-7 object-contain" />
                <div className="font-heading font-bold text-[#114232]">Fortiqa</div>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#2d7a4a]/10 text-[#2d7a4a] text-[10px] font-bold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2d7a4a] animate-pulse" /> Live
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              {FEATURES.map((t) => (
                <div
                  key={t.label}
                  className={`rounded-xl p-4 ${
                    t.accent
                      ? "bg-[#114232] text-[#f4f2eb]"
                      : "bg-[#f4f2eb] border border-[#114232]/8"
                  }`}
                >
                  <div
                    className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${
                      t.accent ? "text-[#d49a36]" : "text-[#5c6360]"
                    }`}
                  >
                    {t.label}
                  </div>
                  <div
                    className={`font-heading font-bold text-xl ${
                      t.accent ? "text-[#f4f2eb]" : "text-[#114232]"
                    }`}
                  >
                    {t.value}
                  </div>
                  <div
                    className={`text-[11px] mt-0.5 ${
                      t.accent ? "text-[#f4f2eb]/70" : "text-[#5c6360]"
                    }`}
                  >
                    {t.sub}
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-[#f4f2eb] border border-[#114232]/8 p-4 mb-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#5c6360] mb-3">
                Recent claims
              </div>
              <div className="space-y-2.5">
                {[
                  { id: "FQC-8821", amt: "KES 8,500", status: "Paid", dot: "bg-[#2d7a4a]", color: "text-[#2d7a4a]" },
                  { id: "FQC-8804", amt: "KES 3,200", status: "Paid", dot: "bg-[#2d7a4a]", color: "text-[#2d7a4a]" },
                  { id: "FQC-8772", amt: "KES 12,000", status: "In review", dot: "bg-[#d49a36]", color: "text-[#d49a36]" },
                ].map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      <span className="font-mono text-xs text-[#114232]">{c.id}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#5c6360] text-xs hidden sm:inline">{c.amt}</span>
                      <span className={`text-xs font-semibold ${c.color}`}>{c.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl bg-[#c15337]/8 border border-[#c15337]/20 p-3.5 flex items-start gap-3">
              <AlertOctagon className="w-4 h-4 text-[#c15337] mt-0.5 shrink-0" />
              <div className="text-xs text-[#114232] leading-relaxed">
                <strong>Fraud alert</strong> — unusual SIM-swap pattern flagged on linked till
                0712-xx-xxxx.{" "}
                <span className="text-[#c15337] font-semibold">Review</span>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-14">
          <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232] mb-5">
            What's inside
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.concat([
              { label: "Payment history", sub: "Every weekly premium", desc: "Search and download every premium charge, with M-Pesa reference numbers and invoice PDFs." },
              { label: "Fraud alerts", sub: "Real-time", desc: "Get notified about SIM-swap patterns, suspicious logins, and unusual claim velocity on your linked tills." },
              { label: "Merchant verification", sub: "KYC status", desc: "Track your business verification, see what's still pending, and re-upload documents if anything was rejected." },
            ]).map((f) => (
              <div
                key={f.label}
                className="rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#c15337] font-bold mb-1.5">
                  {f.label}
                </div>
                <div className="font-heading font-bold text-base text-[#114232] mb-1.5">
                  {f.sub}
                </div>
                <p className="text-sm text-[#5c6360] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
                Try it
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Sign up and your dashboard activates in 60 seconds.
              </h3>
            </div>
            <Link
              to="/signup"
              data-testid="dashboard-preview-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
            >
              Sign up <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
